import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import {
  Users, Tag, Euro, BarChart3, Send, CheckCircle, XCircle, Clock, Eye, Power, Loader2,
  Plus, X, AlertCircle, Award, Copy, Check, Ban, CreditCard, TrendingUp, Search,
  Megaphone, Pencil, Trash2, Minus
} from 'lucide-react';
import { motion } from 'framer-motion';

const TABS = [
  { id: 'influencers', label: 'Influenceurs', icon: Users },
  { id: 'promo-codes', label: 'Codes Promo', icon: Tag },
  { id: 'promo-campaigns', label: 'Campagnes Promo', icon: Megaphone },
  { id: 'payouts', label: 'Paiements', icon: Euro },
  { id: 'stats', label: 'Stats & Classement', icon: BarChart3 },
];

const AdminInfluencers = () => {
  const [tab, setTab] = useState('influencers');

  return (
    <div className="p-6 md:p-8" data-testid="admin-influencers-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Users className="h-6 w-6 text-[#FF6B35]" />Programme Influenceurs
        </h1>
        <p className="text-[#6B6B6E] text-sm mt-1">Gérez les influenceurs, codes promo et paiements</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-[#121214] p-1 rounded-lg border border-[#3A3A3D]/40 overflow-x-auto" data-testid="influencer-tabs">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded text-sm font-medium whitespace-nowrap transition-all ${
              tab === t.id ? 'bg-[#FF6B35] text-black' : 'text-[#B8B8BA] hover:text-white hover:bg-[#1A1A1D]'
            }`} data-testid={`tab-${t.id}`}>
            <t.icon className="h-4 w-4" />{t.label}
          </button>
        ))}
      </div>

      {tab === 'influencers' && <InfluencersTab />}
      {tab === 'promo-codes' && <PromoCodesTab />}
      {tab === 'promo-campaigns' && <PromoCampaignsTab />}
      {tab === 'payouts' && <PayoutsTab />}
      {tab === 'stats' && <StatsTab />}
    </div>
  );
};

/* ============================================================
   TAB 1: INFLUENCEURS
   ============================================================ */
function InfluencersTab() {
  const [influencers, setInfluencers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [search, setSearch] = useState('');

  const fetch = async () => {
    try {
      const res = await api.get('/admin/influencers');
      setInfluencers(res.data.influencers || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetch(); }, []);

  const invite = async () => {
    if (!inviteEmail.trim()) return;
    setInviteLoading(true);
    try {
      await api.post('/admin/influencers/invite', { email: inviteEmail.trim().toLowerCase() });
      setMsg({ type: 'success', text: `Invitation envoyée à ${inviteEmail}` });
      setInviteEmail(''); setShowInvite(false); fetch();
    } catch (e) { setMsg({ type: 'error', text: e.response?.data?.detail || 'Erreur' }); }
    finally { setInviteLoading(false); setTimeout(() => setMsg(null), 4000); }
  };

  const toggleStatus = async (uid, currentStatus) => {
    setActionLoading(uid);
    try {
      const newStatus = currentStatus === 'active' ? 'suspended' : 'active';
      await api.put(`/admin/influencers/${uid}/status`, { status: newStatus });
      fetch();
    } catch (e) { console.error(e); }
    finally { setActionLoading(null); }
  };

  const filtered = influencers.filter(i =>
    !search || i.pseudo?.toLowerCase().includes(search.toLowerCase()) || i.email?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="animate-pulse h-48 bg-[#2A2A2D] rounded-lg" />;

  return (
    <div className="space-y-4" data-testid="influencers-tab">
      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B6B6E]" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher..."
            className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg pl-10 pr-4 py-2 text-white text-sm focus:border-[#FF6B35] focus:outline-none" data-testid="search-influencers" />
        </div>
        <button onClick={() => setShowInvite(true)} className="btn-primary py-2 px-4 flex items-center gap-2 text-sm" data-testid="invite-btn">
          <Plus className="h-4 w-4" />Inviter un influenceur
        </button>
      </div>

      {msg && (
        <div className={`p-3 rounded-lg border flex items-center gap-2 ${msg.type === 'success' ? 'border-[#00FF88]/30 bg-[#00FF88]/10 text-[#00FF88]' : 'border-[#FF3B5C]/30 bg-[#FF3B5C]/10 text-[#FF3B5C]'}`}>
          {msg.type === 'success' ? <CheckCircle className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
          <span className="text-sm">{msg.text}</span>
        </div>
      )}

      {/* Invite Modal */}
      {showInvite && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
          className="bg-[#1A1A1D] p-5 rounded-lg border border-[#FF6B35]/30" data-testid="invite-modal">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-bold text-sm">Inviter un influenceur</h3>
            <button onClick={() => setShowInvite(false)} className="text-[#6B6B6E] hover:text-white"><X className="h-4 w-4" /></button>
          </div>
          <div className="flex gap-2">
            <input type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="Email de l'influenceur"
              className="flex-1 bg-[#222226] border border-[#3A3A3D] rounded px-3 py-2 text-white text-sm focus:border-[#FF6B35] focus:outline-none" data-testid="invite-email" />
            <button onClick={invite} disabled={inviteLoading || !inviteEmail.trim()} className="btn-primary py-2 px-4 text-sm disabled:opacity-50 flex items-center gap-2" data-testid="invite-submit">
              {inviteLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}Envoyer
            </button>
          </div>
        </motion.div>
      )}

      {/* Influencers List */}
      <div className="space-y-3">
        {filtered.length === 0 && <p className="text-[#6B6B6E] text-sm">Aucun influenceur trouvé</p>}
        {filtered.map((inf, i) => (
          <div key={i} className="bg-[#1A1A1D] p-4 rounded-lg border border-[#3A3A3D]/40 hover:border-[#FF6B35]/20 transition-all" data-testid={`influencer-row-${i}`}>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-white font-bold text-sm">{inf.pseudo || 'N/A'}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${
                    inf.status === 'active' ? 'text-[#00FF88] bg-[#00FF88]/10 border-[#00FF88]/30' : 'text-[#FF3B5C] bg-[#FF3B5C]/10 border-[#FF3B5C]/30'
                  }`}>{inf.status === 'active' ? 'Actif' : 'Suspendu'}</span>
                </div>
                <p className="text-[#6B6B6E] text-xs truncate">{inf.email}</p>
              </div>
              <div className="flex items-center gap-4 text-xs">
                <div className="text-center"><p className="text-[#6B6B6E]">Ventes</p><p className="text-white font-bold">{inf.stats?.total_sales || 0}</p></div>
                <div className="text-center"><p className="text-[#6B6B6E]">Commission</p><p className="text-[#00FF88] font-bold">{(inf.stats?.total_commission || 0).toFixed(2)}€</p></div>
                <div className="text-center"><p className="text-[#6B6B6E]">CA</p><p className="text-[#00FFFF] font-bold">{(inf.stats?.total_amount || 0).toFixed(2)}€</p></div>
                <div className="text-center"><p className="text-[#6B6B6E]">Codes</p><p className="text-white font-bold">{inf.promo_codes?.length || 0}</p></div>
              </div>
              <button onClick={() => toggleStatus(inf.user_id, inf.status)} disabled={actionLoading === inf.user_id}
                className={`flex items-center gap-1 text-xs px-3 py-1.5 rounded transition-colors ${
                  inf.status === 'active' ? 'bg-[#FF3B5C]/10 text-[#FF3B5C] hover:bg-[#FF3B5C]/20' : 'bg-[#00FF88]/10 text-[#00FF88] hover:bg-[#00FF88]/20'
                }`} data-testid={`toggle-status-${i}`}>
                {actionLoading === inf.user_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Power className="h-3 w-3" />}
                {inf.status === 'active' ? 'Suspendre' : 'Activer'}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============================================================
   TAB 2: PROMO CODES
   ============================================================ */
function PromoCodesTab() {
  const [codes, setCodes] = useState([]);
  const [influencers, setInfluencers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showAddUsage, setShowAddUsage] = useState(null);
  const [usageClientId, setUsageClientId] = useState('');
  const [usageSearch, setUsageSearch] = useState('');
  const [usageLoading, setUsageLoading] = useState(false);
  const [form, setForm] = useState({ code: '', influencer_id: '', discount_euros: 10, commission_euros: 10, max_total_use: 100, max_per_customer: 1 });
  const [createLoading, setCreateLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const fetch = async () => {
    try {
      const [codesRes, infRes, clientsRes] = await Promise.all([
        api.get('/admin/promo-codes'),
        api.get('/admin/influencers'),
        api.get('/admin/clients-paginated?page=1&per_page=100&search=&subscription_filter=all')
      ]);
      setCodes(codesRes.data.promo_codes || []);
      setInfluencers(infRes.data.influencers || []);
      setClients(clientsRes.data.clients || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetch(); }, []);

  const create = async () => {
    setCreateLoading(true);
    try {
      await api.post('/admin/promo-codes', { ...form, code: form.code.toUpperCase() });
      setMsg({ type: 'success', text: `Code ${form.code.toUpperCase()} créé` });
      setShowCreate(false); setForm({ code: '', influencer_id: '', discount_euros: 10, commission_euros: 10, max_total_use: 100, max_per_customer: 1 }); fetch();
    } catch (e) { setMsg({ type: 'error', text: e.response?.data?.detail || 'Erreur' }); }
    finally { setCreateLoading(false); setTimeout(() => setMsg(null), 4000); }
  };

  const revoke = async (codeId) => {
    setActionLoading(codeId);
    try {
      await api.put(`/admin/promo-codes/${codeId}/revoke`);
      setMsg({ type: 'success', text: 'Code révoqué' }); fetch();
    } catch (e) { console.error(e); }
    finally { setActionLoading(null); setTimeout(() => setMsg(null), 3000); }
  };

  const addUsage = async () => {
    if (!showAddUsage || !usageClientId) return;
    setUsageLoading(true);
    try {
      const res = await api.post(`/admin/promo-codes/${showAddUsage}/add-usage`, { client_id: usageClientId });
      setMsg({ type: 'success', text: res.data?.message || 'Utilisation ajoutée' });
      setShowAddUsage(null); setUsageClientId(''); setUsageSearch(''); fetch();
    } catch (e) { setMsg({ type: 'error', text: e.response?.data?.detail || 'Erreur' }); }
    finally { setUsageLoading(false); setTimeout(() => setMsg(null), 4000); }
  };

  const filteredClients = usageSearch
    ? clients.filter(c => c.full_name?.toLowerCase().includes(usageSearch.toLowerCase()) || c.email?.toLowerCase().includes(usageSearch.toLowerCase())).slice(0, 10)
    : clients.slice(0, 10);

  if (loading) return <div className="animate-pulse h-48 bg-[#2A2A2D] rounded-lg" />;

  return (
    <div className="space-y-4" data-testid="promo-codes-tab">
      <div className="flex justify-between items-center">
        <p className="text-[#6B6B6E] text-sm">{codes.length} code(s) promo</p>
        <button onClick={() => setShowCreate(true)} className="btn-primary py-2 px-4 flex items-center gap-2 text-sm" data-testid="create-code-btn">
          <Plus className="h-4 w-4" />Créer un code
        </button>
      </div>

      {msg && (
        <div className={`p-3 rounded-lg border flex items-center gap-2 ${msg.type === 'success' ? 'border-[#00FF88]/30 bg-[#00FF88]/10 text-[#00FF88]' : 'border-[#FF3B5C]/30 bg-[#FF3B5C]/10 text-[#FF3B5C]'}`}>
          <span className="text-sm">{msg.text}</span>
        </div>
      )}

      {/* Create Form */}
      {showCreate && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
          className="bg-[#1A1A1D] p-5 rounded-lg border border-[#FF6B35]/30" data-testid="create-code-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-bold text-sm">Nouveau code promo</h3>
            <button onClick={() => setShowCreate(false)} className="text-[#6B6B6E] hover:text-white"><X className="h-4 w-4" /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[#B8B8BA] text-xs mb-1 block">Code</label>
              <input type="text" value={form.code} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="MONCODE20"
                className="w-full bg-[#222226] border border-[#3A3A3D] rounded px-3 py-2 text-white text-sm font-mono focus:border-[#FF6B35] focus:outline-none" data-testid="code-input" />
            </div>
            <div>
              <label className="text-[#B8B8BA] text-xs mb-1 block">Influenceur</label>
              <select value={form.influencer_id} onChange={e => setForm({ ...form, influencer_id: e.target.value })}
                className="w-full bg-[#222226] border border-[#3A3A3D] rounded px-3 py-2 text-white text-sm focus:border-[#FF6B35] focus:outline-none" data-testid="code-influencer">
                <option value="">Sélectionner...</option>
                {influencers.map(inf => <option key={inf.user_id} value={inf.user_id}>{inf.pseudo} ({inf.email})</option>)}
              </select>
            </div>
            <div>
              <label className="text-[#B8B8BA] text-xs mb-1 block">Réduction client (€)</label>
              <input type="number" value={form.discount_euros} onChange={e => setForm({ ...form, discount_euros: +e.target.value })} min={0} step="0.01"
                className="w-full bg-[#222226] border border-[#3A3A3D] rounded px-3 py-2 text-white text-sm focus:border-[#FF6B35] focus:outline-none" data-testid="code-discount" />
            </div>
            <div>
              <label className="text-[#B8B8BA] text-xs mb-1 block">Commission influenceur (€)</label>
              <input type="number" value={form.commission_euros} onChange={e => setForm({ ...form, commission_euros: +e.target.value })} min={0} step="0.01"
                className="w-full bg-[#222226] border border-[#3A3A3D] rounded px-3 py-2 text-white text-sm focus:border-[#FF6B35] focus:outline-none" data-testid="code-commission" />
            </div>
            <div>
              <label className="text-[#B8B8BA] text-xs mb-1 block">Limite totale (0=illimité)</label>
              <input type="number" value={form.max_total_use} onChange={e => setForm({ ...form, max_total_use: +e.target.value })} min={0}
                className="w-full bg-[#222226] border border-[#3A3A3D] rounded px-3 py-2 text-white text-sm focus:border-[#FF6B35] focus:outline-none" data-testid="code-max-total" />
            </div>
            <div>
              <label className="text-[#B8B8BA] text-xs mb-1 block">Limite par client</label>
              <input type="number" value={form.max_per_customer} onChange={e => setForm({ ...form, max_per_customer: +e.target.value })} min={0}
                className="w-full bg-[#222226] border border-[#3A3A3D] rounded px-3 py-2 text-white text-sm focus:border-[#FF6B35] focus:outline-none" data-testid="code-max-customer" />
            </div>
          </div>
          <button onClick={create} disabled={createLoading || !form.code || !form.influencer_id}
            className="btn-primary py-2 px-6 mt-4 flex items-center gap-2 text-sm disabled:opacity-50" data-testid="code-submit">
            {createLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}Créer
          </button>
        </motion.div>
      )}

      {/* Codes List */}
      <div className="space-y-2">
        {codes.map((c, i) => (
          <div key={i} className={`bg-[#1A1A1D] p-4 rounded-lg border ${c.revoked ? 'border-[#FF3B5C]/20 opacity-60' : 'border-[#3A3A3D]/40'}`} data-testid={`code-row-${i}`}>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-[#FF6B35] font-mono font-bold">{c.code}</span>
                {c.revoked && <span className="text-xs text-[#FF3B5C] bg-[#FF3B5C]/10 px-2 py-0.5 rounded">Révoqué</span>}
              </div>
              <div className="flex items-center gap-4 text-xs flex-wrap">
                <span className="text-[#B8B8BA]">{c.influencer_pseudo}</span>
                <span className="text-[#00FF88]">-{c.discount_euros}€</span>
                <span className="text-[#00FFFF]">Com. {c.commission_euros}€</span>
                <span className="text-[#FFB800]">{c.use_count} util.</span>
                <span className="text-[#6B6B6E]">Max: {c.max_total_use || '∞'}</span>
              </div>
              {!c.revoked && (
                <div className="flex items-center gap-2">
                  <button onClick={() => { setShowAddUsage(c.id); setUsageClientId(''); setUsageSearch(''); }}
                    className="text-xs text-[#00FF88] hover:bg-[#00FF88]/10 px-3 py-1.5 rounded flex items-center gap-1 transition-colors" data-testid={`add-usage-${i}`}>
                    <Plus className="h-3 w-3" />Ajouter util.
                  </button>
                  <button onClick={() => revoke(c.id)} disabled={actionLoading === c.id}
                    className="text-xs text-[#FF3B5C] hover:bg-[#FF3B5C]/10 px-3 py-1.5 rounded flex items-center gap-1 transition-colors" data-testid={`revoke-${i}`}>
                    {actionLoading === c.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Ban className="h-3 w-3" />}Révoquer
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Add Usage Modal */}
      {showAddUsage && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center" onClick={() => setShowAddUsage(null)}>
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
            onClick={e => e.stopPropagation()} className="bg-[#1A1A1D] p-6 rounded-lg border border-[#3A3A3D] max-w-md w-full mx-4" data-testid="add-usage-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-bold text-sm">Ajouter une utilisation</h3>
              <button onClick={() => setShowAddUsage(null)} className="text-[#6B6B6E] hover:text-white"><X className="h-4 w-4" /></button>
            </div>
            <p className="text-[#B8B8BA] text-xs mb-3">Code : <span className="text-[#FF6B35] font-mono">{codes.find(c => c.id === showAddUsage)?.code}</span></p>
            <div className="mb-3">
              <label className="text-[#B8B8BA] text-xs mb-1 block">Rechercher un client</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B6B6E]" />
                <input type="text" value={usageSearch} onChange={e => setUsageSearch(e.target.value)} placeholder="Nom ou email..."
                  className="w-full bg-[#222226] border border-[#3A3A3D] rounded pl-9 pr-3 py-2 text-white text-sm focus:border-[#FF6B35] focus:outline-none" data-testid="usage-search" />
              </div>
            </div>
            <div className="max-h-48 overflow-y-auto space-y-1 mb-4">
              {filteredClients.map(c => (
                <button key={c.id} onClick={() => setUsageClientId(c.id)}
                  className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${usageClientId === c.id ? 'bg-[#FF6B35]/20 border border-[#FF6B35]/50 text-white' : 'bg-[#222226] text-[#B8B8BA] hover:bg-[#2A2A2D]'}`}
                  data-testid={`usage-client-${c.id}`}>
                  <div className="font-medium truncate">{c.full_name}</div>
                  <div className="text-xs text-[#6B6B6E] truncate">{c.email}</div>
                </button>
              ))}
              {filteredClients.length === 0 && <p className="text-[#6B6B6E] text-xs text-center py-4">Aucun client trouvé</p>}
            </div>
            <p className="text-[#6B6B6E] text-xs mb-3 italic">Le montant sera automatiquement récupéré depuis la dernière transaction du client.</p>
            <button onClick={addUsage} disabled={usageLoading || !usageClientId}
              className="btn-primary w-full py-2 text-sm disabled:opacity-50 flex items-center justify-center gap-2" data-testid="confirm-usage-btn">
              {usageLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Confirmer l'utilisation
            </button>
          </motion.div>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   TAB 2b: PROMO CAMPAIGNS (codes marketing multi-usage, ex: LIGUE12026-27)
   ============================================================ */
function PromoCampaignsTab() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ code: '', phase: '', campaign: '', discount_euros: 10, max_uses: 20, valid_from: '', valid_until: '' });
  const [editForm, setEditForm] = useState({ campaign: '', discount_euros: 0, max_uses: 0, valid_from: '', valid_until: '' });
  const [saveLoading, setSaveLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [msg, setMsg] = useState(null);

  const fetch = async () => {
    try {
      const res = await api.get('/admin/promo-campaigns');
      setCampaigns(res.data.campaigns || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetch(); }, []);

  const notify = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 4000); };

  const toDatetimeLocal = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toISOString().slice(0, 16); } catch { return ''; }
  };

  const create = async () => {
    if (!form.code.trim() || !form.phase.trim() || !form.campaign.trim() || !form.valid_from || !form.valid_until) {
      notify('error', 'Merci de remplir tous les champs'); return;
    }
    setSaveLoading(true);
    try {
      await api.post('/admin/promo-campaigns', {
        ...form, code: form.code.toUpperCase(),
        valid_from: new Date(form.valid_from).toISOString(),
        valid_until: new Date(form.valid_until).toISOString(),
      });
      notify('success', `Campagne ${form.code.toUpperCase()} créée`);
      setShowCreate(false);
      setForm({ code: '', phase: '', campaign: '', discount_euros: 10, max_uses: 20, valid_from: '', valid_until: '' });
      fetch();
    } catch (e) { notify('error', e.response?.data?.detail || 'Erreur'); }
    finally { setSaveLoading(false); }
  };

  const startEdit = (c) => {
    setEditId(c.id);
    setEditForm({
      campaign: c.campaign || '', discount_euros: c.discount_euros, max_uses: c.max_uses,
      valid_from: toDatetimeLocal(c.valid_from), valid_until: toDatetimeLocal(c.valid_until),
    });
  };

  const saveEdit = async () => {
    setSaveLoading(true);
    try {
      await api.put(`/admin/promo-campaigns/${editId}`, {
        ...editForm,
        valid_from: new Date(editForm.valid_from).toISOString(),
        valid_until: new Date(editForm.valid_until).toISOString(),
      });
      notify('success', 'Campagne mise à jour');
      setEditId(null); fetch();
    } catch (e) { notify('error', e.response?.data?.detail || 'Erreur'); }
    finally { setSaveLoading(false); }
  };

  const adjustUsage = async (id, delta) => {
    setActionLoading(id);
    try {
      const res = await api.put(`/admin/promo-campaigns/${id}/adjust-usage`, { delta });
      notify('success', `Compteur ajusté (${res.data.use_count} utilisations)`);
      fetch();
    } catch (e) { notify('error', e.response?.data?.detail || 'Erreur'); }
    finally { setActionLoading(null); }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer définitivement cette campagne ?')) return;
    setActionLoading(id);
    try { await api.delete(`/admin/promo-campaigns/${id}`); notify('success', 'Campagne supprimée'); fetch(); }
    catch (e) { notify('error', e.response?.data?.detail || 'Erreur'); }
    finally { setActionLoading(null); }
  };

  if (loading) return <div className="animate-pulse h-48 bg-[#2A2A2D] rounded-lg" />;

  return (
    <div className="space-y-4" data-testid="promo-campaigns-tab">
      <div className="flex justify-between items-center">
        <p className="text-[#6B6B6E] text-sm">{campaigns.length} campagne(s) — codes marketing multi-usage (ex: LIGUE12026-27)</p>
        <button onClick={() => setShowCreate(true)} className="btn-primary py-2 px-4 flex items-center gap-2 text-sm" data-testid="create-campaign-btn">
          <Plus className="h-4 w-4" />Nouvelle campagne
        </button>
      </div>

      {msg && (
        <div className={`p-3 rounded-lg border flex items-center gap-2 ${msg.type === 'success' ? 'border-[#00FF88]/30 bg-[#00FF88]/10 text-[#00FF88]' : 'border-[#FF3B5C]/30 bg-[#FF3B5C]/10 text-[#FF3B5C]'}`} data-testid="campaign-msg">
          <span className="text-sm">{msg.text}</span>
        </div>
      )}

      {showCreate && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
          className="bg-[#1A1A1D] p-5 rounded-lg border border-[#FF6B35]/30" data-testid="create-campaign-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-bold text-sm">Nouvelle campagne promo</h3>
            <button onClick={() => setShowCreate(false)} className="text-[#6B6B6E] hover:text-white"><X className="h-4 w-4" /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[#B8B8BA] text-xs mb-1 block">Code</label>
              <input type="text" value={form.code} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="LIGUE12026-27"
                className="w-full bg-[#222226] border border-[#3A3A3D] rounded px-3 py-2 text-white text-sm font-mono focus:border-[#FF6B35] focus:outline-none" data-testid="campaign-code" />
            </div>
            <div>
              <label className="text-[#B8B8BA] text-xs mb-1 block">Phase (identifiant)</label>
              <input type="text" value={form.phase} onChange={e => setForm({ ...form, phase: e.target.value })} placeholder="phase1"
                className="w-full bg-[#222226] border border-[#3A3A3D] rounded px-3 py-2 text-white text-sm focus:border-[#FF6B35] focus:outline-none" data-testid="campaign-phase" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-[#B8B8BA] text-xs mb-1 block">Nom de la campagne</label>
              <input type="text" value={form.campaign} onChange={e => setForm({ ...form, campaign: e.target.value })} placeholder="Reprise Ligue 1 2026"
                className="w-full bg-[#222226] border border-[#3A3A3D] rounded px-3 py-2 text-white text-sm focus:border-[#FF6B35] focus:outline-none" data-testid="campaign-name" />
            </div>
            <div>
              <label className="text-[#B8B8BA] text-xs mb-1 block">Réduction (€)</label>
              <input type="number" value={form.discount_euros} onChange={e => setForm({ ...form, discount_euros: +e.target.value })} min={0} step="0.01"
                className="w-full bg-[#222226] border border-[#3A3A3D] rounded px-3 py-2 text-white text-sm focus:border-[#FF6B35] focus:outline-none" data-testid="campaign-discount" />
            </div>
            <div>
              <label className="text-[#B8B8BA] text-xs mb-1 block">Quota max d'utilisations</label>
              <input type="number" value={form.max_uses} onChange={e => setForm({ ...form, max_uses: +e.target.value })} min={1}
                className="w-full bg-[#222226] border border-[#3A3A3D] rounded px-3 py-2 text-white text-sm focus:border-[#FF6B35] focus:outline-none" data-testid="campaign-max-uses" />
            </div>
            <div>
              <label className="text-[#B8B8BA] text-xs mb-1 block">Début de validité</label>
              <input type="datetime-local" value={form.valid_from} onChange={e => setForm({ ...form, valid_from: e.target.value })}
                className="w-full bg-[#222226] border border-[#3A3A3D] rounded px-3 py-2 text-white text-sm focus:border-[#FF6B35] focus:outline-none" data-testid="campaign-valid-from" />
            </div>
            <div>
              <label className="text-[#B8B8BA] text-xs mb-1 block">Fin de validité</label>
              <input type="datetime-local" value={form.valid_until} onChange={e => setForm({ ...form, valid_until: e.target.value })}
                className="w-full bg-[#222226] border border-[#3A3A3D] rounded px-3 py-2 text-white text-sm focus:border-[#FF6B35] focus:outline-none" data-testid="campaign-valid-until" />
            </div>
          </div>
          <button onClick={create} disabled={saveLoading}
            className="btn-primary py-2 px-6 mt-4 flex items-center gap-2 text-sm disabled:opacity-50" data-testid="campaign-submit">
            {saveLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}Créer
          </button>
        </motion.div>
      )}

      <div className="space-y-2">
        {campaigns.length === 0 && <p className="text-[#6B6B6E] text-sm">Aucune campagne promo</p>}
        {campaigns.map((c, i) => (
          <div key={c.id} className={`bg-[#1A1A1D] p-4 rounded-lg border ${c.is_active_now ? 'border-[#00FF88]/30' : 'border-[#3A3A3D]/40 opacity-70'}`} data-testid={`campaign-row-${i}`}>
            {editId === c.id ? (
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[#B8B8BA] text-xs mb-1 block">Nom</label>
                    <input type="text" value={editForm.campaign} onChange={e => setEditForm({ ...editForm, campaign: e.target.value })}
                      className="w-full bg-[#222226] border border-[#3A3A3D] rounded px-3 py-2 text-white text-sm" data-testid={`edit-campaign-name-${i}`} />
                  </div>
                  <div>
                    <label className="text-[#B8B8BA] text-xs mb-1 block">Réduction (€)</label>
                    <input type="number" value={editForm.discount_euros} onChange={e => setEditForm({ ...editForm, discount_euros: +e.target.value })} min={0} step="0.01"
                      className="w-full bg-[#222226] border border-[#3A3A3D] rounded px-3 py-2 text-white text-sm" data-testid={`edit-campaign-discount-${i}`} />
                  </div>
                  <div>
                    <label className="text-[#B8B8BA] text-xs mb-1 block">Quota max</label>
                    <input type="number" value={editForm.max_uses} onChange={e => setEditForm({ ...editForm, max_uses: +e.target.value })} min={1}
                      className="w-full bg-[#222226] border border-[#3A3A3D] rounded px-3 py-2 text-white text-sm" data-testid={`edit-campaign-max-${i}`} />
                  </div>
                  <div />
                  <div>
                    <label className="text-[#B8B8BA] text-xs mb-1 block">Début</label>
                    <input type="datetime-local" value={editForm.valid_from} onChange={e => setEditForm({ ...editForm, valid_from: e.target.value })}
                      className="w-full bg-[#222226] border border-[#3A3A3D] rounded px-3 py-2 text-white text-sm" data-testid={`edit-campaign-from-${i}`} />
                  </div>
                  <div>
                    <label className="text-[#B8B8BA] text-xs mb-1 block">Fin</label>
                    <input type="datetime-local" value={editForm.valid_until} onChange={e => setEditForm({ ...editForm, valid_until: e.target.value })}
                      className="w-full bg-[#222226] border border-[#3A3A3D] rounded px-3 py-2 text-white text-sm" data-testid={`edit-campaign-until-${i}`} />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={saveEdit} disabled={saveLoading} className="btn-primary py-1.5 px-4 text-xs disabled:opacity-50" data-testid={`save-campaign-${i}`}>
                    {saveLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Enregistrer'}
                  </button>
                  <button onClick={() => setEditId(null)} className="btn-secondary py-1.5 px-4 text-xs" data-testid={`cancel-campaign-edit-${i}`}>Annuler</button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-[#FF6B35] font-mono font-bold">{c.code}</span>
                  <span className="text-[#6B6B6E] text-xs">{c.phase}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${c.is_active_now ? 'text-[#00FF88] bg-[#00FF88]/10 border-[#00FF88]/30' : 'text-[#6B6B6E] bg-[#3A3A3D]/20 border-[#3A3A3D]/40'}`}>
                    {c.is_active_now ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <p className="text-[#B8B8BA] text-xs flex-1 truncate">{c.campaign}</p>
                <div className="flex items-center gap-4 text-xs flex-wrap">
                  <span className="text-[#00FF88]">-{c.discount_euros}€</span>
                  <span className="text-[#FFB800]">{c.use_count}/{c.max_uses} util.</span>
                  <span className="text-[#00FFFF] font-semibold">{c.remaining} restants</span>
                  <span className="text-[#6B6B6E]">{new Date(c.valid_from).toLocaleDateString('fr-FR')} → {new Date(c.valid_until).toLocaleDateString('fr-FR')}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => adjustUsage(c.id, -1)} disabled={actionLoading === c.id || c.use_count <= 0}
                    className="text-[#B8B8BA] hover:bg-[#3A3A3D]/30 p-1.5 rounded disabled:opacity-30" title="Retirer 1 utilisation" data-testid={`decrease-usage-${i}`}>
                    <Minus className="h-3 w-3" />
                  </button>
                  <button onClick={() => adjustUsage(c.id, 1)} disabled={actionLoading === c.id}
                    className="text-[#B8B8BA] hover:bg-[#3A3A3D]/30 p-1.5 rounded disabled:opacity-30" title="Ajouter 1 utilisation" data-testid={`increase-usage-${i}`}>
                    <Plus className="h-3 w-3" />
                  </button>
                  <button onClick={() => startEdit(c)} className="text-[#00FFFF] hover:bg-[#00FFFF]/10 p-1.5 rounded" data-testid={`edit-campaign-${i}`}>
                    <Pencil className="h-3 w-3" />
                  </button>
                  <button onClick={() => remove(c.id)} disabled={actionLoading === c.id}
                    className="text-[#FF3B5C] hover:bg-[#FF3B5C]/10 p-1.5 rounded" data-testid={`delete-campaign-${i}`}>
                    {actionLoading === c.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============================================================
   TAB 3: PAYOUTS
   ============================================================ */
function PayoutsTab() {
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [msg, setMsg] = useState(null);
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const fetch = async () => {
    try { const res = await api.get('/admin/influencer-payouts'); setPayouts(res.data.payouts || []); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetch(); }, []);

  const approve = async (id) => {
    setActionLoading(id);
    try { await api.put(`/admin/influencer-payouts/${id}/approve`); setMsg({ type: 'success', text: 'Paiement approuvé' }); fetch(); }
    catch (e) { setMsg({ type: 'error', text: e.response?.data?.detail || 'Erreur' }); }
    finally { setActionLoading(null); setTimeout(() => setMsg(null), 3000); }
  };

  const reject = async (id) => {
    setActionLoading(id);
    try { await api.put(`/admin/influencer-payouts/${id}/reject`, { reason: rejectReason }); setMsg({ type: 'success', text: 'Paiement rejeté - Solde restitué' }); setRejectId(null); setRejectReason(''); fetch(); }
    catch (e) { setMsg({ type: 'error', text: e.response?.data?.detail || 'Erreur' }); }
    finally { setActionLoading(null); setTimeout(() => setMsg(null), 3000); }
  };

  const markPaid = async (id) => {
    setActionLoading(id);
    try { await api.put(`/admin/influencer-payouts/${id}/paid`); setMsg({ type: 'success', text: 'Marqué comme payé' }); fetch(); }
    catch (e) { setMsg({ type: 'error', text: e.response?.data?.detail || 'Erreur' }); }
    finally { setActionLoading(null); setTimeout(() => setMsg(null), 3000); }
  };

  const STATUS = {
    pending: { label: 'En attente', color: 'text-[#FFB800] bg-[#FFB800]/10 border-[#FFB800]/30', icon: Clock },
    approved: { label: 'Approuvé', color: 'text-[#00FFFF] bg-[#00FFFF]/10 border-[#00FFFF]/30', icon: CheckCircle },
    paid: { label: 'Payé', color: 'text-[#00FF88] bg-[#00FF88]/10 border-[#00FF88]/30', icon: CheckCircle },
    rejected: { label: 'Rejeté', color: 'text-[#FF3B5C] bg-[#FF3B5C]/10 border-[#FF3B5C]/30', icon: XCircle },
  };

  if (loading) return <div className="animate-pulse h-48 bg-[#2A2A2D] rounded-lg" />;

  return (
    <div className="space-y-4" data-testid="payouts-tab">
      {msg && (
        <div className={`p-3 rounded-lg border flex items-center gap-2 ${msg.type === 'success' ? 'border-[#00FF88]/30 bg-[#00FF88]/10 text-[#00FF88]' : 'border-[#FF3B5C]/30 bg-[#FF3B5C]/10 text-[#FF3B5C]'}`}>
          <span className="text-sm">{msg.text}</span>
        </div>
      )}

      {/* Reject Modal */}
      {rejectId && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-[#1A1A1D] p-6 rounded-lg border border-[#3A3A3D] max-w-md w-full" data-testid="reject-modal">
            <h3 className="text-white font-bold mb-3">Raison du rejet</h3>
            <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} rows={3} placeholder="Raison (optionnel)"
              className="w-full bg-[#222226] border border-[#3A3A3D] rounded px-3 py-2 text-white text-sm focus:border-[#FF6B35] focus:outline-none mb-3" />
            <div className="flex gap-2">
              <button onClick={() => reject(rejectId)} disabled={actionLoading} className="btn-primary py-2 px-4 text-sm bg-[#FF3B5C] hover:bg-[#FF3B5C]/80" data-testid="reject-confirm">Rejeter</button>
              <button onClick={() => { setRejectId(null); setRejectReason(''); }} className="btn-secondary py-2 px-4 text-sm">Annuler</button>
            </div>
          </div>
        </div>
      )}

      {payouts.length === 0 ? (
        <p className="text-[#6B6B6E] text-sm">Aucune demande de paiement</p>
      ) : (
        <div className="space-y-3">
          {payouts.map((p, i) => {
            const s = STATUS[p.status] || STATUS.pending;
            const Icon = s.icon;
            return (
              <div key={i} className="bg-[#1A1A1D] p-4 rounded-lg border border-[#3A3A3D]/40" data-testid={`payout-row-${i}`}>
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-white font-bold">{p.amount?.toFixed(2)}€</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full border flex items-center gap-1 ${s.color}`}>
                        <Icon className="h-3 w-3" />{s.label}
                      </span>
                    </div>
                    <p className="text-[#B8B8BA] text-xs">{p.influencer_pseudo} ({p.influencer_email})</p>
                    <p className="text-[#6B6B6E] text-xs mt-1">IBAN: {p.iban?.replace(/(.{4})/g, '$1 ').trim()} | {p.holder_name}</p>
                    <p className="text-[#6B6B6E] text-xs">Demandé le {new Date(p.created_at).toLocaleDateString('fr-FR')}</p>
                  </div>
                  <div className="flex gap-2">
                    {p.status === 'pending' && (
                      <>
                        <button onClick={() => approve(p.id)} disabled={actionLoading === p.id}
                          className="text-xs bg-[#00FF88]/10 text-[#00FF88] hover:bg-[#00FF88]/20 px-3 py-1.5 rounded flex items-center gap-1" data-testid={`approve-${i}`}>
                          <Check className="h-3 w-3" />Approuver
                        </button>
                        <button onClick={() => setRejectId(p.id)}
                          className="text-xs bg-[#FF3B5C]/10 text-[#FF3B5C] hover:bg-[#FF3B5C]/20 px-3 py-1.5 rounded flex items-center gap-1" data-testid={`reject-${i}`}>
                          <X className="h-3 w-3" />Rejeter
                        </button>
                      </>
                    )}
                    {p.status === 'approved' && (
                      <button onClick={() => markPaid(p.id)} disabled={actionLoading === p.id}
                        className="text-xs bg-[#00FFFF]/10 text-[#00FFFF] hover:bg-[#00FFFF]/20 px-3 py-1.5 rounded flex items-center gap-1" data-testid={`mark-paid-${i}`}>
                        <CreditCard className="h-3 w-3" />Marquer payé
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ============================================================
   TAB 4: STATS & RANKINGS
   ============================================================ */
function StatsTab() {
  const [stats, setStats] = useState(null);
  const [rankings, setRankings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const [statsRes, rankingsRes] = await Promise.all([
          api.get('/admin/influencer-stats'),
          api.get('/admin/influencer-rankings')
        ]);
        setStats(statsRes.data);
        setRankings(rankingsRes.data.rankings || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  if (loading) return <div className="animate-pulse h-48 bg-[#2A2A2D] rounded-lg" />;

  const ov = stats?.overview || {};

  return (
    <div className="space-y-6" data-testid="stats-tab">
      {/* Overview Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Influenceurs', value: `${ov.active_influencers || 0}/${ov.total_influencers || 0}`, sub: 'Actifs / Total', color: '#FF6B35' },
          { label: 'Codes promo', value: `${ov.active_promo_codes || 0}/${ov.total_promo_codes || 0}`, sub: 'Actifs / Total', color: '#00FFFF' },
          { label: 'Ventes totales', value: ov.total_sales || 0, sub: `${(ov.total_revenue || 0).toFixed(2)}€ CA`, color: '#00FF88' },
          { label: 'Commissions', value: `${(ov.total_commissions || 0).toFixed(2)}€`, sub: `${ov.pending_payouts_count || 0} en attente (${(ov.pending_payouts_amount || 0).toFixed(2)}€)`, color: '#FFB800' },
        ].map((s, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
            className="bg-[#1A1A1D] p-5 rounded-lg border border-[#3A3A3D]/40" data-testid={`overview-${i}`}>
            <p className="text-[#6B6B6E] text-xs font-semibold uppercase mb-2">{s.label}</p>
            <p className="text-2xl font-bold text-white">{s.value}</p>
            <p className="text-[#6B6B6E] text-xs mt-1">{s.sub}</p>
          </motion.div>
        ))}
      </div>

      {/* Top Promo Codes */}
      {stats?.top_promo_codes?.length > 0 && (
        <div className="bg-[#1A1A1D] p-5 rounded-lg border border-[#3A3A3D]/40" data-testid="top-codes">
          <h3 className="text-white font-bold mb-3 flex items-center gap-2"><Tag className="h-4 w-4 text-[#FF6B35]" />Top codes promo</h3>
          <div className="space-y-2">
            {stats.top_promo_codes.map((tc, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-[#3A3A3D]/30 last:border-0">
                <span className="text-[#FF6B35] font-mono text-sm">{tc.code}</span>
                <div className="flex gap-4 text-xs">
                  <span className="text-[#B8B8BA]">{tc.sales} ventes</span>
                  <span className="text-[#00FF88]">{tc.commission?.toFixed(2)}€ com.</span>
                  <span className="text-[#00FFFF]">{tc.revenue?.toFixed(2)}€ CA</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Rankings */}
      <div className="bg-[#1A1A1D] p-5 rounded-lg border border-[#3A3A3D]/40" data-testid="rankings">
        <h3 className="text-white font-bold mb-3 flex items-center gap-2"><Award className="h-4 w-4 text-[#FFB800]" />Classement influenceurs</h3>
        {rankings.length === 0 ? <p className="text-[#6B6B6E] text-sm">Aucune donnée</p> : (
          <div className="space-y-2">
            {rankings.map((r, i) => (
              <div key={i} className="flex items-center justify-between py-2.5 border-b border-[#3A3A3D]/30 last:border-0" data-testid={`rank-${i}`}>
                <div className="flex items-center gap-3">
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                    i === 0 ? 'bg-[#FFB800]/20 text-[#FFB800]' : i === 1 ? 'bg-[#C0C0C0]/20 text-[#C0C0C0]' : i === 2 ? 'bg-[#CD7F32]/20 text-[#CD7F32]' : 'bg-[#3A3A3D]/30 text-[#6B6B6E]'
                  }`}>#{r.rank}</span>
                  <div>
                    <p className="text-white text-sm font-medium">{r.pseudo}</p>
                    <p className="text-[#6B6B6E] text-xs">{r.email}</p>
                  </div>
                </div>
                <div className="flex gap-4 text-xs text-right">
                  <div><p className="text-[#6B6B6E]">Ventes</p><p className="text-white font-bold">{r.total_sales}</p></div>
                  <div><p className="text-[#6B6B6E]">CA</p><p className="text-[#00FFFF] font-bold">{r.total_amount?.toFixed(2)}€</p></div>
                  <div><p className="text-[#6B6B6E]">Commission</p><p className="text-[#00FF88] font-bold">{r.total_commission?.toFixed(2)}€</p></div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminInfluencers;
